import { Order } from '../types/order'

// Client-side geocoding for the delivery board map.
//
// Source: PDOK Locatieserver — the Dutch government's public geocoder over BAG data, the
// same service the webshop resolves street + city from at checkout
// (webshop/src/lib/addressLookup.ts). No API key, no quota to manage, and it sends
// `Access-Control-Allow-Origin: *`, so the terminal calls it directly.
//
// Coordinates are resolved on the terminal and cached per terminal. The planned follow-up
// is the API storing them on the order at creation (tasks/todo.md §0a); when that lands
// only this module changes — the map reads the same GeocodeResult shape.

export interface GeoPoint {
  lat: number
  lng: number
}

export interface GeocodeResult extends GeoPoint {
  // 'address' = the building itself. 'postcode' = the PC6 centroid, used when the exact
  // address is not in BAG (a typo, or a Thuisbezorgd number the split could not recover).
  // Still within a street or two of the door — good enough to spot a bundle, not to drive by.
  precision: 'address' | 'postcode'
}

const PDOK_URL = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/free'
const TIMEOUT_MS = 6000
// A board holds a few dozen orders at most; three in flight keeps a cold cache quick
// without hammering a public service from every terminal at once.
const MAX_CONCURRENT = 3
const CACHE_STORAGE_KEY = 'delivery-geocode-cache'
const CACHE_MAX_ENTRIES = 500

// The restaurant's own door, and the door every delivery route leaves from and returns to —
// the API's routing origin (config/deliveryRouting.ts DELIVERY_ORIGIN) and receipt header.
// "Rademarkt" is the BAG spelling; the receipt's old "Radermarkt" was a typo, since corrected
// in the API's ReceiptService. PDOK needs only postcode + number; street and city are for the
// Google Maps route links (lib/delivery.ts), where the spelling does matter. Single location
// today.
export const RESTAURANT_ADDRESS = {
  street: 'Rademarkt',
  houseNumber: '10',
  postcode: '9711 CV',
  city: 'Groningen',
}

// PDOK indexes postcodes without a space ("9711LM"); a query with the space returns zero
// results, silently. Same rule as the webshop lookup.
const normalizePostcode = (raw: string): string | null => {
  const pc = raw.replace(/\s+/g, '').toUpperCase()
  return /^\d{4}[A-Z]{2}$/.test(pc) ? pc : null
}

// Thuisbezorgd self-delivery orders carry the number INSIDE delivery_street
// ("Kerkstraat 12a") with delivery_house_number NULL — see addressOf in lib/delivery.ts.
// PDOK keys the street on the bare number ("94a" → 94), so the trailing digits of the
// street are recovered as a best effort; when that fails the postcode centroid still
// places the pin in the right neighbourhood.
export const houseNumberOf = (
  order: Pick<Order, 'delivery_street' | 'delivery_house_number'>
): string | null => {
  const explicit = order.delivery_house_number?.match(/\d+/)?.[0]
  if (explicit) return explicit
  // Cut at the first comma before matching: HubRise keeps suffixes verbatim
  // ("Rademarkt 10, 2e etage"), and a trailing-number match on the whole string would
  // confidently answer 2 — a wrong door with a solid pin, worse than no number at all.
  const street = order.delivery_street?.split(',')[0] ?? ''
  return street.match(/\s(\d+)[a-zA-Z-]*\s*$/)?.[1] ?? null
}

// Cache key for an order's address; null when the order has no usable postcode. Keyed on
// postcode + number rather than order id so two orders to one door share a lookup.
export const geocodeKeyOf = (
  order: Pick<Order, 'delivery_street' | 'delivery_house_number' | 'delivery_postcode'>
): string | null => {
  const pc = normalizePostcode(order.delivery_postcode ?? '')
  return pc ? `${pc}|${houseNumberOf(order) ?? ''}` : null
}

// --- cache ---------------------------------------------------------------------------
// null = looked up, nothing found. Kept in memory only, never persisted: a 200 with zero
// docs is what a degraded or re-indexing PDOK also answers, and a persisted null would
// make that door un-pinnable on this terminal until someone clears storage by hand. A
// reload retries it; within a session the poll does not. Transport failures are not
// cached at all (the promise rejects), so an outage is retried on the caller's backoff.

type CacheEntry = GeocodeResult | null

let memory: Map<string, CacheEntry> | null = null

const loadCache = (): Map<string, CacheEntry> => {
  if (memory) return memory
  memory = new Map()
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY)
    if (raw) {
      for (const [key, value] of Object.entries(JSON.parse(raw) as Record<string, CacheEntry>)) {
        memory.set(key, value)
      }
    }
  } catch {
    // Unreadable or blocked storage — run with an empty in-memory cache.
  }
  return memory
}

const remember = (key: string, value: CacheEntry) => {
  const cache = loadCache()
  // Re-insert so Map iteration order doubles as least-recently-written order.
  cache.delete(key)
  cache.set(key, value)
  while (cache.size > CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next().value
    if (oldest === undefined) break
    cache.delete(oldest)
  }
  if (value === null) return
  try {
    const hits = [...cache].filter(([, entry]) => entry !== null)
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(Object.fromEntries(hits)))
  } catch {
    // Quota or private mode — the in-memory copy still serves this session.
  }
}

// --- PDOK ----------------------------------------------------------------------------

// Locatieserver returns the point as WKT: "POINT(lon lat)" — longitude first.
const parsePoint = (wkt?: string): GeoPoint | null => {
  const match = wkt?.match(/^POINT\(\s*([-\d.]+)\s+([-\d.]+)\s*\)$/)
  if (!match) return null
  const lng = Number(match[1])
  const lat = Number(match[2])
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}

// Filter queries rather than free text on purpose: free-text `q=` is relevance-ranked and
// happily answers with a neighbouring house. `fq=` is exact. Throws on transport failure.
const fetchPoint = async (filters: string[]): Promise<GeoPoint | null> => {
  const params = new URLSearchParams()
  for (const filter of filters) params.append('fq', filter)
  params.append('rows', '1')
  params.append('fl', 'centroide_ll')

  const timeout = new AbortController()
  const timer = setTimeout(() => timeout.abort(), TIMEOUT_MS)
  try {
    const res = await fetch(`${PDOK_URL}?${params.toString()}`, { signal: timeout.signal })
    if (!res.ok) throw new Error(`PDOK ${res.status}`)
    const json = (await res.json()) as { response?: { docs?: { centroide_ll?: string }[] } }
    return parsePoint(json.response?.docs?.[0]?.centroide_ll)
  } finally {
    clearTimeout(timer)
  }
}

// --- concurrency ---------------------------------------------------------------------

let active = 0
const waiting: (() => void)[] = []

const acquire = (): Promise<void> =>
  new Promise(resolve => {
    const start = () => {
      active++
      resolve()
    }
    if (active < MAX_CONCURRENT) start()
    else waiting.push(start)
  })

const release = () => {
  active--
  waiting.shift()?.()
}

const inFlight = new Map<string, Promise<GeocodeResult | null>>()

// Transport-failure backoff, module-wide so it survives the board unmounting and
// remounting (navigating away and back must not restart a burst against PDOK mid-outage).
// A 30s poll would otherwise retry every miss every tick for the whole outage.
const RETRY_AFTER_MS = 5 * 60_000
const failedAt = new Map<string, number>()

/**
 * Resolve a Dutch postcode (+ optional house number) to a point. Resolves null when PDOK
 * knows neither the address nor the postcode; REJECTS on transport failure (offline,
 * timeout, PDOK down) and, for the next few minutes, on every retry of that key.
 */
export const geocode = (
  postcode: string,
  houseNumber: string | null
): Promise<GeocodeResult | null> => {
  const pc = normalizePostcode(postcode)
  if (!pc) return Promise.resolve(null)
  const key = `${pc}|${houseNumber ?? ''}`

  const cache = loadCache()
  if (cache.has(key)) return Promise.resolve(cache.get(key) ?? null)
  const pending = inFlight.get(key)
  if (pending) return pending
  const failed = failedAt.get(key)
  if (failed !== undefined && Date.now() - failed < RETRY_AFTER_MS) {
    return Promise.reject(new Error('geocode: backing off after a failed lookup'))
  }

  const task = (async () => {
    await acquire()
    try {
      let result: GeocodeResult | null = null
      if (houseNumber) {
        const point = await fetchPoint(['type:adres', `postcode:${pc}`, `huisnummer:${houseNumber}`])
        if (point) result = { ...point, precision: 'address' }
      }
      if (!result) {
        const point = await fetchPoint(['type:postcode', `postcode:${pc}`])
        if (point) result = { ...point, precision: 'postcode' }
      }
      remember(key, result)
      failedAt.delete(key)
      return result
    } catch (err) {
      failedAt.set(key, Date.now())
      throw err
    } finally {
      release()
      inFlight.delete(key)
    }
  })()
  inFlight.set(key, task)
  return task
}

export const geocodeOrder = (
  order: Pick<Order, 'delivery_street' | 'delivery_house_number' | 'delivery_postcode'>
): Promise<GeocodeResult | null> =>
  order.delivery_postcode
    ? geocode(order.delivery_postcode, houseNumberOf(order))
    : Promise.resolve(null)
